import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '../../../core-components/theme/components/DialogBox/ConfirmationDialog';
import { useDeleteLogbook } from '../hooks/useDeleteLogbook';
import { LogbooksPage } from '../types/listTypes';

export default function DeleteLogbookModal({ handleClose, data, queryKey }: any) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { id } = data;
    const deleteMutation = useDeleteLogbook({
        onMutate: async (id: string) => {
            await queryClient.cancelQueries({ queryKey });
            const previousData = queryClient.getQueryData<InfiniteData<LogbooksPage>>(queryKey);
            if (previousData) {
                queryClient.setQueryData<InfiniteData<LogbooksPage>>(queryKey, {
                    ...previousData,
                    pages: previousData.pages.map((page, index) => ({
                        ...page,
                        logbooks: page.logbooks.filter(
                            logbook => logbook.id !== id
                        ),
                        totalCount: index === 0 ? page.totalCount - 1 : page.totalCount,
                    })),
                });
            }
            return { previousData };
        },
        onError: (err: Error, id: string, context: any) => {
            if (context?.previousData) {
                queryClient.setQueryData(queryKey, context.previousData);
            }
        },
    }, queryKey);

    const handleDeleteLogbook = (id: string) => {
        deleteMutation.mutate(id);
    };

    const actions = useMemo(
        () => [
            { text: t('common.cancel', 'Cancel'), handler: handleClose, fullWidth: false, color: '#E0E0E0', textColor: '#000' },
            { text: t('common.delete', 'Delete'), handler: () => handleDeleteLogbook(id), fullWidth: false, color: '#FF0000', textColor: '#FFF', disabled: deleteMutation.isPending },
        ],
        [handleClose, handleDeleteLogbook, deleteMutation.isPending, id]
    );

    return (
        <ConfirmationDialog
            open={true}
            actions={actions}
            handleClose={handleClose}
            title={t('logbook.deleteLogbookTitle', 'Delete Logbook')}
            description={t('logbook.deleteLogbookDescription', 'It will be deleted from everywhere it is used, are you sure you want to delete it?')}
        />
    );
}